/**
 * file-selekt - file upload library, entry point.
 *
 * Usage:
 *   g.fileSelekt('[file-selekt]');   // init all (returns instances)
 *   g.fileSelekt('#avatar', { ... }); // one element + options
 *   g.fileSelekt.get('#avatar');      // instance (also: el.fileSelekt)
 *
 * Options resolution (later wins): g.fileSelekt.defaults < init options <
 * `file-selekt-*` attributes (flat scalars, house rules: kebab->camelCase,
 * "true"/"false" -> boolean, numbers -> numbers, bare attribute -> true).
 *
 * Depends on genus (settings.api/apiToken/filesUrl, g.warning, g.translate).
 * Server contract + file jsonb: docs/libs/file-selekt.md.
 */
import { FileSelekt } from './file-selekt.js';

// Bump on every meaningful change to the library.
const VERSION = '0.6.0';

const defaults = {
	mode: 'simple',        // simple | dropzone | image | image-editor | gallery
	multiple: null,        // null = from the multiple attribute (dropzone/gallery default true)
	accept: '',            // 'jpg,png' | 'image' | 'image,video' | mixed; '' = anything
	endpoint: '/frm/files/upload', // the framework upload helper (real API, session-gated)
	params: {},            // extra data sent with every upload (context)
	maxSize: 0,            // MB per file; 0 = unlimited
	maxFiles: 0,           // multiple only; 0 = unlimited
	resize: 2560,          // client-side downscale for images (long edge px); 0 = off
	aspect: '',            // the ONE display + crop ratio (image frame, gallery tiles, editor frame): '' = square, '1:1', '16:9', number
	shape: 'rounded',      // image modes: rounded | circle
	cover: false,          // gallery: cover badge + "set cover" action (cover: true in jsonb)
	captions: false,       // gallery: caption input per image (caption in jsonb)
	rows: true,            // dropzone: false = the drop area alone, no rows under it (the page owns the list; the zone shows the flight)
	clearable: true,
	readonly: false,
	placeholder: '',
	thumbKey: 'thumb',     // variants key preferred for previews
};

const camel = (s) => s.replace(/-([a-z])/g, (m, c) => c.toUpperCase());

const coerce = (v) => {
	if (v === '' || v === 'true') return true;
	if (v === 'false') return false;
	if (v !== '' && !isNaN(v) && String(Number(v)) === v) return Number(v);
	return v;
};

function attrOptions(el) {
	const opts = {};
	const params = {};
	for (const attr of el.attributes) {
		let name = null;
		if (attr.name.startsWith('data-file-selekt-')) name = attr.name.slice(17);
		else if (attr.name.startsWith('file-selekt-')) name = attr.name.slice(12);
		if (!name) continue;
		const value = coerce(attr.value);
		if (name.startsWith('param-')) params[camel(name.slice(6))] = value;
		else opts[camel(name)] = value;
	}
	if (Object.keys(params).length) opts.params = params;
	return opts;
}

function fileSelekt(target, options) {
	let els;
	if (typeof target === 'string') els = Array.from(document.querySelectorAll(target));
	else if (target instanceof Element) els = [target];
	else els = Array.from(target || []);

	const instances = els.map((el) => el.fileSelekt || new FileSelekt(el, { ...defaults, ...options, ...attrOptions(el) }));
	return instances.length === 1 ? instances[0] : instances;
}

fileSelekt.version = () => VERSION;
fileSelekt.defaults = defaults;
fileSelekt.get = (target) => {
	const el = typeof target === 'string' ? document.querySelector(target) : target;
	return (el && el.fileSelekt) || null;
};

if (window.g) {
	window.g.fileSelekt = fileSelekt;
	window.g.registerLib('file-selekt');
} else {
	window.fileSelekt = fileSelekt;
}
